import React, { useState, useEffect, useRef } from 'react';
import {
  useSlateStatic,
  ReactEditor,
} from 'slate-react';
import isUrl from 'is-url';
import { TwitterTweetEmbed } from 'react-twitter-embed';

import serverInterface from 'server-interface';

import { isObject } from 'utilities';

import useOnFocus from 'generics/hooks/on-focus';

import Helpers from '../../helpers';

import BaseElement from '../base';

import {
  extractVideoId
} from './utilities';

import './index.scss';

const providerTypes = {
  TWITTER: 'Twitter'
};

function URLEmbed(props) {

  const element = props.element;

  const inputReference = useRef(null);
  const loadingReference = useRef(false);
  const editor = useSlateStatic();
  const elementPath = ReactEditor.findPath(editor, element);

  const [rootState, setRootState] = useState({
    ...element.urlEmbedOptions
  });

  useOnFocus(inputReference);
  // useOnKeypressWithTarget(inputReference, 'Enter', handleURLInputEnterKey);

  useEffect(function onLoad() {

    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onElementChange() {

    setRootState({
      ...rootState,
      ...element.urlEmbedOptions
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element]);

  useEffect(function onRootStateChange() {
    tryLoadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootState]);

  function initialize() {

    tryLoadDetails();

  }

  function swtichToFileNode(details) {

    Helpers.file.toggle(editor, {
      file: {
        ...details,
        url: details.uri
      },
      showFileDialog: false,
      fileType: details.type
    }, { at: elementPath });

  }

  function switchToYouTubeNode(videoId) {

    Helpers.urlEmbed.convertToYouTubeBlock(editor, videoId, elementPath);

  }

  function switchToParagraphNode(url) {

    Helpers.urlEmbed.convertToParagraph(editor, url, url, elementPath);

  }

  function normalizeOGImage(ogImage) {

    if (Array.isArray(ogImage)) {

      if (ogImage.length > 0) {
        return ogImage[0];
      } else {
        return null;
      }

    }

    return ogImage;

  }

  function updateURIDetails(url, details) {

    Helpers.setAttributes(editor, {
      urlEmbedOptions: {
        ...rootState,
        url,
        dataLoaded: true,
        dataAvailable: true,
        data: {
          ...details,
          ogImage: normalizeOGImage(details.ogImage)
        }
      }
    }, elementPath);

  }

  async function tryLoadDetails() {

    if (rootState.dataLoaded === true) {
      return;
    }

    if (rootState.inputValid !== true) {
      return;
    }

    if (loadingReference.current === true) {
      return;
    }
    loadingReference.current = true;

    let url = rootState.url;

    const details = await serverInterface.system.getURIDetails({ url });

    if (details.type === 'UNKNOWN') {
      switchToParagraphNode(details.uri);
      return;
    }

    if (details.mimetype !== 'text/html') {
      swtichToFileNode(details);
      return;
    }

    const data = details.metadata;

    if (data.error === true) {
      switchToParagraphNode(url);
      return;
    }

    const videoId = extractVideoId(url);
    if (videoId !== null) {
      switchToYouTubeNode(videoId);
      return;
    }

    if (data.result === null) {
      const videoId = extractVideoId(url);
      if (videoId !== null) {
        switchToYouTubeNode(videoId);
      } else {
        switchToParagraphNode(url);
      }
      return;
    }

    updateURIDetails(url, data.result);

  }

  function handleURLInputEnterKey() {

    let url = rootState.url;

    if (isUrl(url) === false) {

      let maybeURL = `https://${url}`;

      if (isUrl(maybeURL) !== true) {
        return;
      }

      url = maybeURL;

    }

    Helpers.setAttributes(editor, {
      urlEmbedOptions: {
        ...rootState,
        inputValid: true,
        url
      }
    }, elementPath);

    Helpers.tryInsertNewLine(editor);

  }

  function handleURLInputChange(url) {

    setRootState({
      ...rootState,
      url
    });

  }

  function renderSpinner() {

    return <ht-spinner size="small" />;

  }

  function tryRenderTwitterCard(data) {

    if (data.providerType !== providerTypes.TWITTER) {
      return;
    }

    const twitterCardContainerAttributes = {
      className: 'twitter-card-container'
    };

    const twitterTweetEmbedProperties = {
      tweetId: data.url.split('/').pop()
    };

    return <div {...twitterCardContainerAttributes}>
      <TwitterTweetEmbed {...twitterTweetEmbedProperties} />
    </div>;

  }

  function renderOpenGraphTitle(data) {

    return <span className="url-embed-data-title ellipsis">{data.ogTitle}</span>;

  }

  function renderOpenGraphDescription(data) {

    return <span className="url-embed-data-description ellipsis sub-text">{data.ogDescription}</span>;

  }

  function renderOpenGraphSiteName(data) {

    return <span className="url-embed-data-site-name ellipsis sub-text">{data.ogSiteName}</span>;

  }

  function renderOpenGraphImage(data) {

    if (isObject(data.ogImage) === false) {
      return;
    }

    const imageAttributes = {
      className: 'url-embed-data-image ellipsis',
      src: data.ogImage.url,
      alt: data.ogTitle + ' image'
    };

    return <img {...imageAttributes} />;

  }

  function tryRenderURLEmbedCard(data) {

    if (data.providerType === providerTypes.TWITTER) {
      return;
    }

    const urlEmbedLinkAttributes = {
      className: 'url-embed-data',
      href: data.requestUrl,
      target: '_blank'
    };

    return <a {...urlEmbedLinkAttributes}>

      <div className="url-embed-data-image-details">

        {renderOpenGraphImage(data)}

      </div>

      <div className="url-embed-data-text-details">

        {renderOpenGraphSiteName(data)}

        {renderOpenGraphTitle(data)}

        {renderOpenGraphDescription(data)}

      </div>

    </a>;

  }

  function tryRenderDetails() {

    if (rootState.dataAvailable === false) {
      return;
    }

    const data = rootState.data;

    if (typeof data === 'undefined') {
      return;
    }

    return <div className="url-embed-details">
      {tryRenderTwitterCard(data)}
      {tryRenderURLEmbedCard(data)}
    </div>;

  }

  function tryRenderURL() {

    if (rootState.dataLoaded === true) {
      return;
    }

    const urlEmbedLinkAttributes = {
      className: 'url-embed-temporary-url',
      href: rootState.url,
      target: '_blank'
    };

    return <div className="url-embed-temporary-link-content-container">

      <div className="url-embed-temporary-url ellipsis">

        <a {...urlEmbedLinkAttributes}>{rootState.url}</a>

      </div>

      <div className="url-embed-temporary-spinner">

        {renderSpinner()}

      </div>

    </div>;

  }

  function renderURLInputBox() {

    const urlFormContainerAttributes = {
      className: 'url-embed-form-container',
      contentEditable: false
    };

    const inputAttributes = {
      type: 'text',
      ref: inputReference,
      placeholder: 'Type or paste your URL, then press Enter to embed it',
      value: rootState.url,
      onChange(event) {
        handleURLInputChange(event.target.value.trim());
      },
      onPaste(event) {
        event.stopPropagation();
        return false;
      }
    };

    const formAttributes = {
      className: 'url-embed-form',
      onSubmit(event) {
        event.preventDefault();
        event.stopPropagation();
        handleURLInputEnterKey();
      }
    };

    return <div {...urlFormContainerAttributes}>

      <form {...formAttributes}>

        <input {...inputAttributes} />

      </form>

    </div>;

  }

  function renderURLEmbed() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'url-embed-element'
    };

    if (rootState.inputValid !== true) {
      return <div {...mainAttributes}>
        {renderURLInputBox()}
        {props.children}
      </div>;
    }

    return <div {...mainAttributes}>
      {tryRenderURL()}
      {tryRenderDetails()}
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'url-embed-wrapper'
    };

    return <BaseElement {...baseElementProperties}>
      {renderURLEmbed()}
    </BaseElement>;

  }

  return renderMain();

}

export default URLEmbed;